import { useState, useEffect, useRef } from "react";
import { Search, AlertCircle, Gift, Mail } from "lucide-react";
import { useAnalyticsQuery } from "@/hooks/useAnalyticsQuery";
import { analyticsApi } from "@/services/apiClient";
import { MAX_RANGE_DAYS, daysAgoISODate, todayISODate } from "@/stores/filterStore";
import TopUsersTable from "@/components/TopUsersTable";
import ExportButton from "@/components/ExportButton";
import CopyButton from "@/components/CopyButton";
import SectionRangePicker from "@/components/SectionRangePicker";
import MobileUserList from "@/components/MobileUserList";
import { useIsMobile } from "@/hooks/useMediaQuery";
import EngineGroupFilterDropdown from "@/components/EngineGroupFilterDropdown";
import ToggleGroup from "@/components/ToggleGroup";
import InfoTooltip from "@/components/InfoTooltip";
import KpiCard, { KpiRow } from "@/components/KpiCard";
import UserTrendChart from "@/components/UserTrendChart";
import Drawer from "@/components/Drawer";
import BulkCreditGrant from "@/components/BulkCreditGrant";
import { fmt, UNMAPPED_ENGINE } from "@/lib/format";
import { FILTER_GROUPS, engineKey } from "@/lib/engineFilterGroups";
import { balanceStatus } from "@/lib/thresholds";
import { rebucketSignupSeries, formatBucketLabel } from "@/lib/timeseries";
import Panel from "./Panel";
import InternalUsersView from "./InternalUsersView";
import LegacyInternalView from "./LegacyInternalView";
import Skeleton from "./Skeleton";

function defaultRange() {
  return { from: daysAgoISODate(MAX_RANGE_DAYS - 1), to: todayISODate() };
}

const SIGNUP_GRANULARITY_OPTIONS = [
  { id: "day",   label: "일별" },
  { id: "week",  label: "주별" },
  { id: "month", label: "월별" },
];
// "구간별/누적" — 매출·마진 추이·도구별 사용량 탭과 동일 라벨로 통일(2026-07-17, 예전엔
// 여기만 "신규/누적"이라 같은 개념(그 기간만 vs 누적)이 탭마다 다르게 불렸음). 차트 자체의
// 라인 이름(UserTrendChart 의 label prop)이 "신규 가입자"/"누적 가입자"로 이미 구체적이라
// 토글 라벨까지 "신규"일 필요는 없다.
const SIGNUP_MODE_OPTIONS = [
  { id: "period",     label: "구간별" },
  { id: "cumulative", label: "누적" },
];

// "미선택" — 알려진 도구+엔진 조합(FILTER_GROUPS) 밖의 사용량(engine 미기록·미분류 채팅 등).
// 그룹 트리에는 안 들어가는 낱개 옵션이라 extraOptions 로 따로 붙인다.
const UNMAPPED_OPTION = { tool: "", engine: UNMAPPED_ENGINE, label: "미선택" };

// 정렬 프리셋 칩(최근사용순/잔액많은순 등 5개) 대신 표 칼럼 헤더를 직접 눌러 오름/내림차순
// 토글하는 표준 방식으로 변경(2026-07-15). 칼럼별 정렬 기준(문자열/숫자)과 처음 눌렀을 때
// 방향(가입일·잔액·사용량·충전횟수·최근사용은 큰 값이 먼저 보이는 게 자연스러워 desc,
// 이메일은 알파벳순이 자연스러워 asc)만 여기 한 곳에 정의 — TopUsersTable 은 이 설정을
// 그대로 받아 헤더를 렌더링만 한다.
export const USER_SORT_COLUMNS = {
  email:      { accessor: (u) => (u.email || u.userId || "").toLowerCase(), type: "string", defaultDir: "asc" },
  // createdAtIso(시각 포함)가 있으면 그걸로 정렬 — 같은 날짜 안에서도 가입 순서가 유지된다.
  createdAt:  { accessor: (u) => u.createdAtIso || u.createdAt || "", type: "string", defaultDir: "desc" },
  balance:    { accessor: (u) => u.balance ?? 0, type: "number", defaultDir: "desc" },
  ncUsed:     { accessor: (u) => u.ncUsed ?? 0, type: "number", defaultDir: "desc" },
  chargeCount:{ accessor: (u) => u.chargeCount ?? 0, type: "number", defaultDir: "desc" },
  lastSeen:   { accessor: (u) => u.lastSeen || "", type: "string", defaultDir: "desc" },
};

// 사용자 목록의 페이지네이션은 TopUsersTable 내부 Pagination 이 단일 소유(총 N명·페이지
// 크기 선택·번호). CSV 내보내기는 페이지와 무관하게 필터링된 전체(userCsv=users 기준).

function sortUsers(users, sortKey, sortDir) {
  const col = USER_SORT_COLUMNS[sortKey];
  if (!col) return users;
  const sorted = [...users].sort((a, b) => {
    const av = col.accessor(a);
    const bv = col.accessor(b);
    const cmp = col.type === "string" ? av.localeCompare(bv) : av - bv;
    return sortDir === "asc" ? cmp : -cmp;
  });
  return sorted;
}

// 도구 칩 여러 개 동시 선택(OR) — 백엔드 /analytics/users/top 는 한 번에 한 tool/engine만
// 받으므로, 선택된 칩 수만큼 병렬 호출해서 유저 단위로 합친다(2026-07-15, 다중 선택 요청 반영).
// 선택이 0개면(기본값) 필터 없이 한 번만 호출해 전체 사용량을 그대로 받는다 — 매번 19개
// 조합으로 병렬 호출하는 게 기본값이 되지 않도록 "전체 선택"과는 의도적으로 다르게 둔다.
// ncUsed/requestCount는 선택된 도구들의 합, lastSeen은 그중 최신값 — balance/가입일처럼
// 도구와 무관한 값은 첫 응답 값을 그대로 쓴다(모든 응답에서 동일).
// fresh=true 면 ?fresh=1 을 붙여 백엔드 로스터 캐시(3분)를 건너뛰고 최신 잔액을 받는다 —
// 크레딧 지급/회수 직후 리페치에서만 켠다(평소엔 캐시로 저렴하게).
async function fetchMergedTopUsers(params, signal, selectedToolEngines, fresh) {
  const base = fresh ? { ...params, fresh: 1 } : params;
  if (selectedToolEngines.length === 0) {
    return analyticsApi.topUsers({ ...base, metric: "ncUsed" }, signal);
  }
  const results = await Promise.all(
    selectedToolEngines.map((te) =>
      analyticsApi.topUsers({ ...base, metric: "ncUsed", tool: te.tool || undefined, engine: te.engine || undefined }, signal)
    )
  );
  const merged = new Map();
  for (const res of results) {
    for (const u of res.users || []) {
      const existing = merged.get(u.userId);
      if (existing) {
        existing.ncUsed += u.ncUsed;
        existing.requestCount += u.requestCount;
        if (u.lastSeen > existing.lastSeen) existing.lastSeen = u.lastSeen;
      } else {
        merged.set(u.userId, { ...u });
      }
    }
  }
  return { ...results[0], users: Array.from(merged.values()) };
}

// 모바일(<768px)은 조회 중심 카드 리스트(MobileUserList)로 대체 — desktop 대형 표를
// 그대로 축소하지 않는다(P2, 2026-09-17). 위험 운영 액션은 데스크톱 권장.
export default function CreditsSection() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileUserList /> : <DesktopCreditsSection />;
}

function DesktopCreditsSection() {
  // 가입자 추이 — 아래 유저 로스터(usersRange)와 별개의 카드/범위/단위. 예전엔 한 Panel 안에
  // 날짜피커 2개가 나란히 있어서 뭐가 뭔지 헷갈렸는데(2026-07-17 UI 리뷰), 아예 별도 카드로
  // 분리해서 "이 둘은 서로 다른 걸 조회하는 컨트롤"이라는 게 시각적으로 분명해지게 했다.
  const [signupRange, setSignupRange] = useState(defaultRange);
  const [signupGranularity, setSignupGranularity] = useState("week");
  const [signupMode, setSignupMode] = useState("period");

  const [usersRange, setUsersRange] = useState(defaultRange);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [selectedToolEngines, setSelectedToolEngines] = useState([]); // [{ tool, engine }] — 빈 배열 = 필터 없음(전체 사용량)
  const [creditRefreshTick, setCreditRefreshTick] = useState(0);
  // 기본값 "최근 사용순" — 예전 프리셋의 기본값과 동일하게 유지.
  const [sort, setSort] = useState({ key: "lastSeen", dir: "desc" });
  const [search, setSearch] = useState(""); // 이메일/userId 검색 — /admin 페이지 흡수(2026-07-15)
  const [lowBalanceOnly, setLowBalanceOnly] = useState(false); // "소진 임박 N명" 배지 클릭 시 필터(2026-07-15)
  const [bulkGrantOpen, setBulkGrantOpen] = useState(false); // 일괄 크레딧 지급 드로어(2026-07-15)
  // 메일 발송 대상 선택(2026-07-28) — 검색어·필터를 바꿔도 선택이 유지되도록 표 밖에서 소유.
  // 이메일 소문자 기준 Set. 발송은 /mail-compose 새 창에 쿼리로 넘긴다.
  // 평소엔 체크박스를 숨기고, "선택" 버튼을 눌러 선택 모드에 들어갔을 때만 표에 노출.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState(new Set());

  function toggleSelectMode() {
    setSelectMode((on) => {
      if (on) setSelectedEmails(new Set()); // 모드 종료 시 선택 초기화
      return !on;
    });
  }
  // 다음 리페치 한 번만 캐시 우회(fresh=1) — 지급/회수 성공 직후 최신 잔액을 즉시 반영.
  const freshOnceRef = useRef(false);
  // 낙관적 잔액 override(userId→잔액) — 지급/회수 성공 즉시 표 숫자를 바꿔 "새로고침 없이 바로"
  // 보이게 한다. 백그라운드 fresh 리페치가 서버 값을 들고 오면(topUsers 변경) 비워서 일원화.
  const [balanceOverrides, setBalanceOverrides] = useState({});

  // 지급/회수 성공 시 호출 — 캐시 우회 플래그를 세우고 리페치를 트리거한다.
  function refreshCreditsLive() {
    freshOnceRef.current = true;
    setCreditRefreshTick((t) => t + 1);
  }

  // 지급/회수 성공 콜백: 화면 잔액을 즉시(낙관적) 반영 + 서버 재조회로 대조.
  // change = { userIds: [...], amount: 증감액(부호 포함) }
  function handleGrantSuccess(change) {
    const { userIds, amount } = change || {};
    if (userIds?.length && Number.isFinite(amount)) {
      setBalanceOverrides((prev) => {
        const next = { ...prev };
        const byId = new Map((topUsers?.users || []).map((u) => [u.userId, u]));
        for (const id of userIds) {
          const cur = next[id] != null ? next[id] : byId.get(id)?.balance;
          if (cur != null) next[id] = cur + amount;
        }
        return next;
      });
    }
    refreshCreditsLive();
  }

  function toggleEmail(email) {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  // 헤더 체크박스 — 현재 화면 목록(검색·필터 적용) 전원이 선택돼 있으면 그들만 해제,
  // 아니면 전부 추가. 다른 검색어에서 선택해둔 사람은 건드리지 않는다.
  function toggleAllVisible(visibleEmails) {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      const allSelected = visibleEmails.every((e) => next.has(e));
      for (const e of visibleEmails) {
        if (allSelected) next.delete(e);
        else next.add(e);
      }
      return next;
    });
  }

  function openMailCompose() {
    const emails = [...selectedEmails].join(",");
    window.open(`/mail-compose?emails=${encodeURIComponent(emails)}`, "_blank", "width=760,height=920");
  }

  function handleSortChange(key) {
    setSort((prev) => (prev.key === key
      ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { key, dir: USER_SORT_COLUMNS[key].defaultDir }));
  }

  function toggleToolEngine(opt) {
    setSelectedToolEngines((prev) => {
      const key = engineKey(opt);
      const exists = prev.some((p) => engineKey(p) === key);
      return exists ? prev.filter((p) => engineKey(p) !== key) : [...prev, opt];
    });
  }

  function toggleGroup(superGroup, engines) {
    setSelectedToolEngines((prev) => {
      const groupKeys = new Set(engines.map(engineKey));
      const allSelected = engines.every((e) => prev.some((p) => engineKey(p) === engineKey(e)));
      if (allSelected) return prev.filter((p) => !groupKeys.has(engineKey(p)));
      const withoutGroup = prev.filter((p) => !groupKeys.has(engineKey(p)));
      return [...withoutGroup, ...engines];
    });
  }

  function toggleExpand(superGroup) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(superGroup)) next.delete(superGroup);
      else next.add(superGroup);
      return next;
    });
  }

  const { data: signupData, loading: signupLoading, error: signupError } = useAnalyticsQuery(
    (p, s) => analyticsApi.overview({ ...p, includeSignup: 1 }, s),
    [signupRange.from, signupRange.to],
    signupRange
  );
  const signupSeries = rebucketSignupSeries(signupData?.signupTrend, signupGranularity)
    .map((r) => ({ ...r, bucketLabel: formatBucketLabel(r.bucket, signupGranularity) }));
  const signupValueKey = signupMode === "cumulative" ? "cumulativeUsers" : "newUsers";
  const signupLabel = signupMode === "cumulative" ? "누적 가입자" : "신규 가입자";

  const { data: topUsers, loading: usersLoading, error: usersError } = useAnalyticsQuery(
    (p, s) => {
      const fresh = freshOnceRef.current;
      freshOnceRef.current = false; // 한 번만 소비
      return fetchMergedTopUsers(p, s, selectedToolEngines, fresh);
    },
    [usersRange.from, usersRange.to, JSON.stringify(selectedToolEngines), creditRefreshTick],
    usersRange
  );

  const searchQ = search.trim().toLowerCase();
  // 낙관적 override 를 얹은 목록 — 지급 직후 서버 재조회가 오기 전까지 바뀐 잔액을 즉시 보여준다.
  const rawUsers = (topUsers?.users || []).map((u) =>
    u.userId in balanceOverrides ? { ...u, balance: balanceOverrides[u.userId] } : u
  );
  const searchedUsers = searchQ
    ? rawUsers.filter((u) => (u.email || "").toLowerCase().includes(searchQ) || u.userId.toLowerCase().includes(searchQ))
    : rawUsers;
  // 배지의 N은 lowBalanceOnly 토글과 무관하게 항상 "검색 결과 중 소진 임박자 수"를 보여준다
  // (토글을 켜면 숫자가 사라지는 게 아니라 그 숫자만큼 걸러 보여주는 게 목적이라).
  const lowBalanceCount = searchedUsers.filter((u) => balanceStatus(u.balance) === "bad").length;
  const balanceFilteredUsers = lowBalanceOnly
    ? searchedUsers.filter((u) => balanceStatus(u.balance) === "bad")
    : searchedUsers;
  const users = sortUsers(balanceFilteredUsers, sort.key, sort.dir);
  const userCsv = [
    ["#", "이메일", "userId", "가입일시(KST)", "현재 잔액", "사용량(선택 기간)", "충전 횟수", "마지막 사용(KST)"],
    ...users.map((u, i) => [i + 1, u.email || "", u.userId, u.createdAtIso ? fmt.kstDateTime(u.createdAtIso) : (u.createdAt || ""), u.balance, u.ncUsed, u.chargeCount, u.lastSeen ? fmt.kstDateTime(u.lastSeen) : ""]),
  ];

  // 페이지 리셋은 TopUsersTable 내부 Pagination 이 users prop 변경 시 자체 처리한다.

  // 서버에서 새 목록이 도착하면(지급 직후 fresh 리페치 완료 등) 낙관적 override 를 비워
  // 서버 값으로 일원화한다 — 낙관치와 서버값이 어긋나 오래 남는 걸 방지.
  useEffect(() => {
    if (topUsers) setBalanceOverrides({});
  }, [topUsers]);

  // 탭 상단 KPI — 기간 지표(신규 가입·활성 사용자)는 위 "가입자 추이" 카드의 날짜 범위
  // (signupRange)를 따르고, 총 가입자·소진 임박은 시점 지표라 범위와 무관하다. 추가 API
  // 호출 없이 이미 받아둔 overview 응답과 로스터에서 계산.
  const kpi = signupData?.kpi;
  const newSignups = (signupData?.signupTrend || []).reduce((s, r) => s + (r.newUsers || 0), 0);
  const allLowBalanceCount = rawUsers.filter((u) => balanceStatus(u.balance) === "bad").length;

  const [audience, setAudience] = useState("b2c");
  const [internalSource, setInternalSource] = useState("nova");

  return (
    <div className="space-y-4">
      {/* B2C 고객 / 사내 구성원 세그먼트 (2026-07-20) — 내부 무료 사용자는 잔액·충전
          개념이 없어 같은 표에 섞으면 왜곡. 같은 자리에서 뷰만 전환한다. */}
      <div className="flex justify-end">
        <ToggleGroup
          options={[{ id: "b2c", label: "B2C 고객" }, { id: "internal", label: "사내 구성원 (무료)" }]}
          value={audience} onChange={setAudience} />
      </div>

      {audience === "internal" && (
        <div className="space-y-4">
          {/* 사내 두 세계: 신규 노바(무료 개통) vs 레거시 AI-NOVA-V1 인하우스 7툴
              (옛 dashboard.sedaily.ai 통합, 2026-07-20). 같은 CMS 풀 기자들이지만
              사용처가 갈려 뷰를 나눈다. */}
          <div className="flex justify-end">
            <ToggleGroup
              options={[
                { id: "nova", label: "노바 (신규·무료)" },
                { id: "legacy", label: "레거시 사내툴" },
              ]}
              value={internalSource} onChange={setInternalSource} />
          </div>
          {internalSource === "nova" && <InternalUsersView />}
          {internalSource === "legacy" && <LegacyInternalView />}
        </div>
      )}

      {audience === "b2c" && (<>
      {/* Stripe 스타일 한 줄 stat bar — 박스 4개 대신 구분선으로 나눈 가로 바 */}
      <div className="flex divide-x divide-ink-200 overflow-hidden rounded-xl border border-ink-200 bg-white shadow-sm">
        <StatBarItem
          label="총 가입자"
          value={kpi?.totalSignups != null ? fmt.int(kpi.totalSignups) : "—"}
          accent="#2563eb"
        />
        <StatBarItem
          label="신규 가입"
          value={signupData ? fmt.int(newSignups) : "—"}
          sub="이 기간"
          accent="#10b981"
        />
        <StatBarItem
          label="활성 사용자"
          value={kpi?.uniqueUsers != null ? fmt.int(kpi.uniqueUsers) : "—"}
          sub="이 기간"
          accent="#6366f1"
        />
        <StatBarItem
          label="소진 임박"
          value={topUsers ? `${fmt.int(allLowBalanceCount)}명` : "—"}
          sub="잔액 100 미만"
          accent={allLowBalanceCount > 0 ? "#f97316" : "#9ca3af"}
          warn={allLowBalanceCount > 0}
        />
      </div>
      <Panel
        title={
          <span className="inline-flex items-center gap-1.5">
            가입자 추이
            <InfoTooltip
              items={[
                { term: "집계 대상", desc: "nova(ainova.news) B2C 가입자만" },
                { term: "제외", desc: '내부 SSO 풀(자동 프로비저닝이라 "가입" 개념 없음), "test"로 시작하는 QA 계정' },
              ]}
            />
          </span>
        }
        action={
          <div className="flex flex-wrap items-center gap-2">
            <SectionRangePicker from={signupRange.from} to={signupRange.to} onChange={(from, to) => setSignupRange({ from, to })} />
            <ToggleGroup options={SIGNUP_GRANULARITY_OPTIONS} value={signupGranularity} onChange={setSignupGranularity} />
            <ToggleGroup options={SIGNUP_MODE_OPTIONS} value={signupMode} onChange={setSignupMode} />
          </div>
        }
      >
        {signupError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            데이터를 불러오지 못했습니다 — {signupError.message} ({signupError.code}).
          </div>
        ) : signupLoading && !signupData ? (
          <Skeleton h={56} variant="chart" />
        ) : (
          <div className="animate-fade-in">
            <UserTrendChart series={signupSeries} valueKey={signupValueKey} label={signupLabel} color="#22c55e" />
          </div>
        )}
      </Panel>

      <Panel>
        <div className="relative mb-3">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="이메일 또는 User ID로 검색"
            className="w-full rounded-xl bg-ink-50 py-3 pl-10 pr-4 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-brand-300"
          />
        </div>

        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <EngineGroupFilterDropdown
              groups={FILTER_GROUPS}
              selected={selectedToolEngines}
              expandedGroups={expandedGroups}
              extraOptions={[UNMAPPED_OPTION]}
              onToggleEngine={toggleToolEngine}
              onToggleGroup={toggleGroup}
              onToggleExpand={toggleExpand}
              onClear={() => setSelectedToolEngines([])}
              onSelectAll={() => setSelectedToolEngines([
                ...FILTER_GROUPS.flatMap((g) => g.engines.map(({ tool, engine }) => ({ tool, engine }))),
                { tool: UNMAPPED_OPTION.tool, engine: UNMAPPED_OPTION.engine },
              ])}
            />
            {!usersLoading && (lowBalanceCount > 0 || lowBalanceOnly) && (
              <button
                type="button"
                onClick={() => setLowBalanceOnly((v) => !v)}
                className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-2 text-xs font-medium transition-colors ${
                  lowBalanceOnly ? "bg-red-600 text-white" : "bg-red-50 text-red-600 hover:bg-red-100"
                }`}
              >
                <AlertCircle className="h-3.5 w-3.5" />
                소진 임박 {lowBalanceCount}명
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={toggleSelectMode}
              className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-2 text-xs font-medium ${
                selectMode ? "bg-brand-600 text-white hover:bg-brand-700" : "bg-ink-50 text-ink-600 hover:bg-ink-100"
              }`}
            >
              <Mail className="h-3.5 w-3.5" />
              {selectMode ? "선택 취소" : "선택"}
            </button>
            {selectMode && (
              <button
                type="button"
                onClick={openMailCompose}
                disabled={selectedEmails.size === 0}
                title={selectedEmails.size === 0 ? "표에서 수신자를 먼저 선택하세요" : undefined}
                className="inline-flex items-center gap-1 rounded-lg bg-brand-50 px-2.5 py-2 text-xs font-medium text-brand-700 hover:bg-brand-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Mail className="h-3.5 w-3.5" />
                메일 발송{selectedEmails.size > 0 && ` (${selectedEmails.size}명)`}
              </button>
            )}
            <button
              type="button"
              onClick={() => setBulkGrantOpen(true)}
              className="inline-flex items-center gap-1 rounded-lg bg-ink-50 px-2.5 py-2 text-xs font-medium text-ink-600 hover:bg-ink-100"
            >
              <Gift className="h-3.5 w-3.5" />
              일괄 크레딧 지급
            </button>
            <SectionRangePicker from={usersRange.from} to={usersRange.to} onChange={(from, to) => setUsersRange({ from, to })} />
            <CopyButton rows={userCsv} />
            <ExportButton filename="users-credit" rows={userCsv} />
          </div>
        </div>

        <Drawer open={bulkGrantOpen} onClose={() => setBulkGrantOpen(false)} title="일괄 크레딧 지급">
          <BulkCreditGrant onSuccess={handleGrantSuccess} />
        </Drawer>

        {usersError && (
          <div className="mb-3 rounded-xl bg-red-50 px-3 py-2.5 text-sm text-red-700">
            데이터를 불러오지 못했습니다 — {usersError.message} ({usersError.code}). 위 날짜 범위가 올바른지 확인해 주세요.
          </div>
        )}
        {/* 이미 데이터가 있으면(필터/기간만 바뀐 재조회) 스켈레톤으로 안 갈아치우고 기존 표를
            그대로 유지 — 최초 로드일 때만 스켈레톤(2026-07-15, 재조회마다 깜빡이던 문제 개선). */}
        {!usersError && (usersLoading && !topUsers ? <Skeleton h={72} variant="table" /> : (
          <div className="animate-fade-in">
            {/* 페이지네이션은 TopUsersTable 내부 Pagination 이 단일 소유(총 N명·페이지 크기 선택·
                번호). 예전 CreditsSection 자체 페이지네이션은 이중 집계('총 30명' vs '1-30/70')를
                만들어 제거(2026-07-20). 전체 필터링 리스트를 그대로 넘긴다. */}
            <TopUsersTable
              users={users}
              sort={sort}
              onSortChange={handleSortChange}
              onAdjusted={handleGrantSuccess}
              selectMode={selectMode}
              selectedEmails={selectedEmails}
              onToggleEmail={toggleEmail}
              onToggleAllVisible={toggleAllVisible}
            />
          </div>
        ))}
      </Panel>
      </>)}
    </div>
  );
}

// Stripe 스타일 한 줄 stat bar 아이템
// 왼쪽 컬러 보더 + 라벨 + 큰 숫자 + 보조 텍스트
function StatBarItem({ label, value, sub, accent = "#2563eb", warn = false }) {
  return (
    <div className="relative flex flex-1 flex-col px-5 py-4 min-w-0">
      {/* 왼쪽 컬러 액센트 선 */}
      <div
        className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full"
        style={{ backgroundColor: accent }}
      />
      <div className="text-[12px] font-medium text-ink-500">{label}</div>
      <div
        className={`mt-1 text-2xl font-bold tabular-nums tracking-tight ${warn ? "" : "text-ink-900"}`}
        style={warn ? { color: accent } : undefined}
      >
        {value}
      </div>
      {sub && <div className="mt-0.5 text-[11px] text-ink-400">{sub}</div>}
    </div>
  );
}
